import { Fragment } from "react";

import type { User } from "~/shared.types";
import { formatDateOfBirth, getAge } from "~/shared/utils/date-of-birth";
import { formatStoredPhoneNumber } from "~/shared/utils/format-stored-phone-number";
import { ModalActions, type ModalAction } from "~/ui/common/Modal/ModalActions";
import { formatPatientId, getBannerPatientId } from "../utils/patient-id";
import { CopyableValue } from "./CopyableValue";

interface BannerSegment {
  id: string;
  label: string;
  value: string;
  fieldName: string;
  successTitle: string;
}

const getAgeAndSex = (user: User) => {
  const age = getAge(user.dateOfBirth);

  return (
    [age === null ? null : `${age}yo`, user.sexAtBirth]
      .filter(Boolean)
      .join(" ") || null
  );
};

const buildPatientSegments = (user: User): BannerSegment[] => {
  const ageAndSex = getAgeAndSex(user);
  const dateOfBirth = formatDateOfBirth(user.dateOfBirth);
  const patientId = getBannerPatientId(user);
  const segments: BannerSegment[] = [];

  if (ageAndSex) {
    segments.push({
      id: "ageAndSex",
      label: ageAndSex,
      value: ageAndSex,
      fieldName: "age and sex",
      successTitle: "Age and sex copied",
    });
  }

  if (dateOfBirth) {
    segments.push({
      id: "dateOfBirth",
      label: dateOfBirth,
      value: dateOfBirth,
      fieldName: "date of birth",
      successTitle: "Date of birth copied",
    });
  }

  if (patientId) {
    segments.push({
      id: "patientId",
      label: formatPatientId(patientId.patientId, patientId.isPrimary),
      value: patientId.patientId,
      fieldName: "patient ID",
      successTitle: "Patient ID copied",
    });
  }

  return segments;
};

const buildContactSegments = (user: User): BannerSegment[] => {
  const phone = user.phoneNumber
    ? formatStoredPhoneNumber(user.phoneNumber)
    : "";
  const segments: BannerSegment[] = [];

  if (user.email) {
    segments.push({
      id: "email",
      label: user.email,
      value: user.email,
      fieldName: "email",
      successTitle: "Email copied",
    });
  }

  if (phone) {
    segments.push({
      id: "phone",
      label: phone,
      value: phone,
      fieldName: "phone",
      successTitle: "Phone copied",
    });
  }

  return segments;
};

const BannerLine = ({ segments }: { segments: BannerSegment[] }) => {
  if (!segments.length) return null;

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-base font-medium">
      {segments.map((segment, index) => (
        <Fragment key={segment.id}>
          {index > 0 && <span aria-hidden="true">|</span>}
          <CopyableValue
            label={segment.label}
            value={segment.value}
            fieldName={segment.fieldName}
            successTitle={segment.successTitle}
            className="break-all"
          />
        </Fragment>
      ))}
    </div>
  );
};

interface UserDetailsBannerProps {
  user: User;
  actions: ModalAction[];
}

export const UserDetailsBanner = ({
  user,
  actions,
}: UserDetailsBannerProps) => {
  const fullName = `${user.firstName} ${user.lastName}`;

  return (
    <div className="bg-indigo flex items-start justify-between gap-4 px-8 py-6 text-white">
      <div className="flex min-w-0 flex-col gap-3">
        <CopyableValue
          label={fullName}
          value={fullName}
          fieldName="name"
          successTitle="Name copied"
          className="text-lg font-medium"
        />
        <div className="flex flex-col gap-1">
          <BannerLine segments={buildPatientSegments(user)} />
          <BannerLine segments={buildContactSegments(user)} />
        </div>
      </div>
      {!!actions.length && (
        <div className="shrink-0">
          <ModalActions actions={actions} variant="inverted" />
        </div>
      )}
    </div>
  );
};
